import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export type TemplateType = 
  | "order_receipt" 
  | "customer_receipt" 
  | "conference_bill" 
  | "cash_movement" 
  | "cash_close"
  | "cancellation_receipt";

export interface PrintTemplate {
  id?: string;
  restaurant_id: string;
  template_type: TemplateType;
  title_bold: boolean;
  title_size: "normal" | "double";
  header_text: string | null;
  items_bold: boolean;
  items_size: "normal" | "double";
  totals_bold: boolean;
  totals_size: "normal" | "double";
  footer_text: string | null;
  show_software_footer: boolean;
  separator_char: string;
}

export const DEFAULT_TEMPLATE: Omit<PrintTemplate, "restaurant_id" | "template_type"> = {
  title_bold: true,
  title_size: "double",
  header_text: null,
  items_bold: true,
  items_size: "double",
  totals_bold: true,
  totals_size: "normal",
  footer_text: "Obrigado pela preferencia!",
  show_software_footer: true,
  separator_char: "=",
};

export const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  order_receipt: "Pedido (Cozinha/Bar)",
  customer_receipt: "Via do Cliente",
  conference_bill: "Conferência de Conta",
  cash_movement: "Sangria/Suprimento",
  cash_close: "Fechamento de Caixa",
  cancellation_receipt: "Cancelamento",
};

export function usePrintTemplates() {
  const { activeRestaurant } = useRestaurantContext();
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    if (!activeRestaurant?.id) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("print_templates")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id);

      if (error) throw error;

      // Cast the data properly
      const typedData = (data || []).map(item => ({
        ...item,
        template_type: item.template_type as TemplateType,
        title_size: item.title_size as "normal" | "double",
        items_size: item.items_size as "normal" | "double",
        totals_size: item.totals_size as "normal" | "double",
      }));

      setTemplates(typedData);
    } catch (error) {
      console.error("Error fetching print templates:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const getTemplate = useCallback((templateType: TemplateType): PrintTemplate => {
    const existing = templates.find(t => t.template_type === templateType);
    if (existing) return existing;

    // Return default template if not found
    return {
      ...DEFAULT_TEMPLATE,
      restaurant_id: activeRestaurant?.id || "",
      template_type: templateType,
    };
  }, [templates, activeRestaurant?.id]);

  const saveTemplate = useCallback(async (template: PrintTemplate): Promise<boolean> => {
    if (!activeRestaurant?.id) return false;

    try {
      const templateData = {
        ...template,
        restaurant_id: activeRestaurant.id,
      };

      if (template.id) {
        // Update existing
        const { error } = await supabase
          .from("print_templates")
          .update(templateData)
          .eq("id", template.id);

        if (error) throw error;
      } else {
        // Insert new (upsert to handle conflicts)
        const { error } = await supabase
          .from("print_templates")
          .upsert(templateData, {
            onConflict: "restaurant_id,template_type",
          });

        if (error) throw error;
      }

      await fetchTemplates();
      toast.success("Template salvo com sucesso!");
      return true;
    } catch (error) {
      console.error("Error saving print template:", error);
      toast.error("Erro ao salvar template");
      return false;
    }
  }, [activeRestaurant?.id, fetchTemplates]);

  return {
    templates,
    isLoading,
    getTemplate,
    saveTemplate,
    refetch: fetchTemplates,
  };
}

// Standalone function to fetch template for printing (without hook)
export async function fetchPrintTemplate(
  restaurantId: string, 
  templateType: TemplateType
): Promise<PrintTemplate> {
  try {
    const { data, error } = await supabase
      .from("print_templates")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .eq("template_type", templateType)
      .maybeSingle();

    if (error) throw error;

    if (data) {
      return {
        ...data,
        template_type: data.template_type as TemplateType,
        title_size: data.title_size as "normal" | "double",
        items_size: data.items_size as "normal" | "double",
        totals_size: data.totals_size as "normal" | "double",
      };
    }

    // Return default if not found
    return {
      ...DEFAULT_TEMPLATE,
      restaurant_id: restaurantId,
      template_type: templateType,
    };
  } catch (error) {
    console.error("Error fetching print template:", error);
    return {
      ...DEFAULT_TEMPLATE,
      restaurant_id: restaurantId,
      template_type: templateType,
    };
  }
}
